import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDates } from "../../../hooks/useDates";
import GenericPromises from "../../../api/GenericPromises";
import { useFormatNumber } from "../../../hooks/useFormatNumber";
import useSnackBar from "../../../components/Commons/SnackBar/useSnackBar";
import { useCallback, useEffect, useState } from "react";
import { GridColDef, GridRowsProp, GridTreeNodeWithRender, GridValueGetterParams } from "@mui/x-data-grid";
import { Menuitem } from "../../../interfaces/Security/menu";
import { usePermissions } from "../../../hooks/usePermissions";
import { Spinner } from "../../../components/Commons/Spinner/Spinner";
import { Header } from "../../../components/Header";
import DataTable from "../../../components/Tables/GridTableMaterialUI/DataTable";
import { PrimaryButton } from "../../../theme/buttons";
import { PurchaseOrder } from "../../../interfaces/Purchases/Orders/purchaseOrders";

export const TablePurchaseOrders = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { TimeConverter } = useDates();
  const { GenericGetResource, GenericGetResourceGeneric } = GenericPromises();
  const { setFormatNumberFromTable } = useFormatNumber();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [unauthorized, setUnauthorized] = useState(true);
  const [purchaseOrdersData, setPurchaseOrdersData] = useState<GridRowsProp>([]);
  const [resourceScreen, setResourceScreen] = useState<Menuitem>();
  const [messageSnack, setMessageSnack] = useState("");
  const [myPreferences, setMyPreferences] = useState({});
  const { GetResourceByUrl } = usePermissions();
  const [dataPurchaseOrderSelected, setDataPurchaseOrderSelected] = useState<GridRowsProp>([]);
  const [defaultFilter, setDefaultFilter] = useState<{
    columnfilter: string;
    value: string;
  }[]>([]);
  const [columns, setColumns] = useState<GridColDef<PurchaseOrder>[]>([
    { field: 'purchase_order_name', headerName: t("purchaseorders.fields.purchase_order_name"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'purchase_order_date', headerName: t("purchaseorders.fields.purchase_order_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'supplier_business_name', headerName: t("purchaseorders.fields.supplier_business_name"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'sales_order', headerName: t("purchaseorders.fields.sales_order"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'total_amount', headerName: t("purchaseorders.fields.total_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return params.value ? setFormatNumberFromTable(params.value ?? 0) : undefined;
      },
    },
    {
      field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
  ]);

  const methodUsed = useCallback(() => {
    switch (location.state.method) {
      case "add":
        {
          showSnackBar(t("generic.snackbar.add"), "success");
          break;
        }
      case "delete":
        {
          showSnackBar(t("generic.snackbar.delete"), "success");
          break;
        }
    }

  }, [location.state, t]);

  const loadPreferences = async () => {
    let myPreferences = await localStorage.getItem("grid-purchaseorders");
    if (myPreferences != null) {
      setMyPreferences(JSON.parse(myPreferences));
    }
  }

  const loadColumnsOrder = async () => {
    let myOrderColumns = await localStorage.getItem("grid-purchaseorders-columns");
    if (myOrderColumns != null) {
      let myJson = JSON.parse(myOrderColumns);
      for (let index = 0; index < myJson.length; index++) {
        const element = myJson[index];
        if (element['type'] === 'date') {
          if (element.field === 'purchase_order_date' || element.field === 'date_mail_send') {
            element.headerName = t(`purchaseorders.fields.${element.field}`);
            element.valueGetter = function (params: GridValueGetterParams<PurchaseOrder, any, GridTreeNodeWithRender>) {
              return TimeConverter(params.value);
            };
          }
          else {
            element.headerName = t(`generic.${element.field}`);
            element.valueGetter = function (params: GridValueGetterParams<PurchaseOrder, any, GridTreeNodeWithRender>) {
              return TimeConverter(params.value);
            };
          }
        }
        else {
          if (element.field === 'total_amount' || element.field === 'total_company_currency_amount') {
            element.valueGetter = function (params: GridValueGetterParams<PurchaseOrder, any, GridTreeNodeWithRender>) {
              return params.value ? setFormatNumberFromTable(params.value ?? 0) : undefined;
            }
          }
          else
            if (element.field === 'currency_code') { element.headerName = t("currencies.title-view"); }
            else if (element.field === 'total_company_currency_amount') {
              element.headerName = t("companies.configuration.fields.total_company_currency_amount");
              element.valueGetter = function (params: GridValueGetterParams<PurchaseOrder, any, GridTreeNodeWithRender>) {
                return setFormatNumberFromTable(params.value ?? 0);
              }
            }
            else if (element.field === 'last_update_user') { element.headerName = t(`generic.${element.field}`); }
            else if (element.field === 'supplier_business_name') { element.headerName = t(`purchaseorders.fields.supplier_business_name`); }
            else {
              element.headerName = t(`purchaseorders.fields.${element.field}`);
            }
        }
      }
      setColumns(myJson);
    }
  }

  const onSelectedRows = (rowsIds: any) => {
    let selectedPurchaseOrders = rowsIds.map((element: number) => purchaseOrdersData.find((elementFind) => elementFind["purchase_order_id"] === element));
    setDataPurchaseOrderSelected(selectedPurchaseOrders);
  };

  useEffect(() => {
    GenericGetResourceGeneric("/companies", "/gcompanies").then((response) => {
      document.title = `${response.data.data[0].comercial_name} - ${t("purchaseorders.title")}`;
    })

    if (location.state !== null) {
      if (location.state.method) {
        methodUsed();
      }
      else if (location.state.row && location.state.row.purchase_order_id) {
        navigate("/purchaseorders/view", { state: { row: location.state.row } })
      }
    }

    let myResources = [
      GenericGetResource(`/purchaseorders`)
    ];

    Promise.all(myResources)
      .then(async (responses) => {
        setPurchaseOrdersData(responses[0].data.data);

        await GetResourceByUrl(`/purchaseorders`)
          .then((response1) => {
            setResourceScreen((prev) => response1);
          })
          .catch((error) => {
            showSnackBar(error.message, "error");
            setUnauthorized(false);
          });

        loadColumnsOrder();
        loadPreferences();
        setDataLoaded(true);
      }
      ).catch((error) => {
        showSnackBar(error.message, "error");
        setUnauthorized(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (purchaseOrdersData) {
      setIsLoadingDataTable(true);
    }
  }, [purchaseOrdersData]);

  return (
    <>
      {!unauthorized && <div className='screen-container d-flex flex-column justify-content-center align-items-center'> <img alt='ERROR' style={{ height: "20rem", width: "20rem" }} src={require("../../../assets/img/error.webp")} /> <h2>{messageSnack}</h2> </div>}
      {unauthorized && !dataLoaded && <Spinner />}
      {dataLoaded && resourceScreen?.read && <div className='screen-container'>
        <Header
          title={t("purchaseorders.title")}
          child={
            <div className='d-flex flex-row-reverse my-1'>
              {resourceScreen?.create === true &&
                <PrimaryButton variant='outlined' className="m-1" onClick={() => { navigate("add", { state: { ...location.state } }) }}>{t("generic.buttons.add")}</PrimaryButton>
              }
            </div>
          }
        />
        <div className="d-flex justify-content-center">
          {!isLoadingDataTable && <Spinner />}
          {isLoadingDataTable &&
            <DataTable
              columns={columns}
              setColumns={setColumns}
              data={purchaseOrdersData}
              entityId={"purchase_order_id"}
              entity={`PurchaseOrders`}
              preferences={myPreferences}
              namePreferences={"grid-purchaseorders"}
              nameOrderColumns={"grid-purchaseorders-columns"}
              selectRows={true}
              countColumns={["total_company_currency_amount"]}
              onRowSelectionModelChange={(rowsIds) => onSelectedRows(rowsIds)}
              initFilter={defaultFilter}
            />
          }
        </div>
        <SnackbarComponent />
      </div>
      }
    </>
  )
}
